import Sidebar from "../components/sidebar";
import AddReview from "../components/addreview";
import { useState, useEffect } from 'react';
import { fetchTestimonials, deleteTestimonial, editTestimonial } from '../services/reviewHandler';

interface Review {
  id: string;
  name: string;
  body: string;
}

const ReviewManagement = () => {
  const [reviews, setReviews] = useState<Review[]>([]); 
  const [showAddReviews, setShowAddReviews] = useState(false); 
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [editedReview, setEditedReview] = useState<Partial<Review>>({});

  useEffect(() => {
    if (!showAddReviews) {
      const loadReviews = async () => {
        try {
          const fetchedReviews = await fetchTestimonials();
          setReviews(fetchedReviews);
        } catch (error) {
          console.error("Error loading reviews:", error);
        }
      };
  
      loadReviews();
    }
  }, [showAddReviews]); 
  

  const handleDelete = async (id: string) => {
    try {
      await deleteTestimonial(id); 
      setReviews(reviews.filter(review => review.id !== id)); 
    } catch (error) {
      console.error('Error deleting review:', error);
    }
  };

  const handleSaveEdit = async () => {
    try {
      if (isEditing) {
        const updatedReview = {
          id: editedReview.id,
          name: editedReview.name,
          body: editedReview.body,
        };
  
        await editTestimonial(
          updatedReview.id, // Ensure 'id' is passed first
          updatedReview.body,
          updatedReview.name
        );
  
        // Update the reviews array in state
        setReviews(reviews.map(review => (review.id === isEditing ? { ...review, ...updatedReview } : review)));
        setIsEditing(null); // Reset editing state
      }
    } catch (error) {
      console.error('Error updating review:', error);
    }
  };

  const handleEdit = (review: Review) => {
    setIsEditing(review.id);
    setEditedReview(review); 
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditedReview(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div>
      <Sidebar />
      {showAddReviews && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
          <div className="max-w-4xl w-3/4">
            <AddReview onClose={() => setShowAddReviews(false)} />
          </div>
        </div>
      )}
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">Review Management</h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage all the reviews received.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              onClick={() => setShowAddReviews(true)}
            >
              Add a new review
            </button>
          </div>
        </div>
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Review
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {reviews.map((review) => (
                      <tr key={review.id}>
                        {isEditing === review.id ? (
                          <>
                            <td className="px-3 py-4 text-sm text-gray-500">
                              <input
                                type="text"
                                name="name"
                                value={editedReview.name || ''}
                                onChange={handleInputChange}
                                className="w-full border rounded p-1"
                              />
                            </td>
                            <td className="px-3 py-4 text-sm text-gray-500">
                              <textarea
                                name="body"
                                value={editedReview.body || ''}
                                onChange={handleInputChange}
                                className="w-full border rounded p-1 h-24"
                                rows={3}
                              />
                            </td>
                            <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <button
                                onClick={handleSaveEdit}
                                className="text-green-600 hover:text-green-900"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setIsEditing(null)}
                                className="text-red-600 hover:text-red-900 ml-2"
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="whitespace-normal break-words px-3 py-4 text-sm text-gray-500">{review.name}</td>
                            <td className="whitespace-normal break-words px-3 py-4 text-sm text-gray-500">{review.body}</td>
                            <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <button
                                onClick={() => handleEdit(review)}
                                className="text-indigo-600 hover:text-indigo-900"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(review.id)}
                                className="text-red-600 hover:text-red-900 ml-4"
                              >
                                Delete
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewManagement;